/**
 * Sentence Builder drill strategy (its own stack: sentence + prompt +
 * distractors). The student sees the prompt and puts word tiles in order;
 * the built sentence is a typed answer checked with word order KEPT and
 * punctuation ignored (tiles carry none: a trailing '.' would give the
 * last word away).
 */
import type { Try } from 'src/types/course/step';
import { itemsToArray } from 'src/shared/utilities/array';
import { isTypedAnswerCorrect } from 'src/modules/Step/views/StepView/utilities/typedAnswer';
import { escapeText } from './escapeText';
import type {
  DrillContext,
  DrillItemMap,
  DrillResultRowMap,
  DrillStrategy,
} from './types';

type SentenceItem = DrillItemMap['SentenceBuilder'];

function correctAnswer(item: SentenceItem): string | null {
  return item?.sentence?.trim() || null;
}

const errorRow: DrillResultRowMap['SentenceBuilder'] = {
  id: -1,
  source: undefined,
  correct: undefined,
  tries: [],
};

export const sentenceBuilderStrategy: DrillStrategy<'SentenceBuilder'> = {
  kind: 'SentenceBuilder',
  // The prompt is shown, not spoken; the built sentence is spoken when right.
  speaksQuestion: false,
  speaksAnswer: true,
  errorRow,

  correctAnswer: (item) => correctAnswer(item),

  /** Word order is the point; punctuation can't be built (tiles have none). */
  isCorrect(answer, item, ctx: DrillContext) {
    const correct = correctAnswer(item);
    if (!answer || !correct) return false;
    return isTypedAnswerCorrect(answer, correct, {
      ...ctx.typingRules,
      ignoreWordOrder: false,
      ignorePunctuation: true,
    });
  },

  // HTML members are rendered via v-html: text is escaped (BUG-21).
  feedbackAnswerHtml(answer) {
    return escapeText(answer);
  },

  feedbackCorrectAnswer(item) {
    return escapeText(correctAnswer(item));
  },

  feedbackSubtitle(questionText) {
    return escapeText(questionText);
  },

  helpAnswer(item) {
    return escapeText(correctAnswer(item));
  },

  spokenAnswer(answer) {
    return answer;
  },

  questionText(item) {
    return item?.prompt?.trim() || '';
  },

  resultRow(item, id, tries) {
    const correct = correctAnswer(item) ?? undefined;
    return {
      id,
      source: item?.prompt?.trim() || undefined,
      correct,
      tries: tries.map((tryItem: Try) => ({
        duration: tryItem.duration,
        answer: tryItem.isCorrect ? correct : tryItem.answer,
        isCorrect: tryItem.isCorrect,
        isHelp: tryItem.isHelp,
        ...(tryItem.isTimeout ? { isTimeout: true } : {}),
      })),
    };
  },
};

export type SentenceTile = { id: number; word: string };

/** Leading/trailing signs of a word ('bike.' → 'bike'; "don't" is kept). */
const EDGE_SIGNS = /^[\p{P}\p{S}]+|[\p{P}\p{S}]+$/gu;

/** The sentence's words: split on spaces, without edge punctuation. */
export function sentenceWords(sentence: string | null | undefined): string[] {
  return (sentence ?? '')
    .split(/\s+/)
    .map((word) => word.replace(EDGE_SIGNS, ''))
    .filter(Boolean);
}

/**
 * The shuffled word tiles: the sentence's words plus the distractors
 * ('|'-separated). A multi-word sentence is never dealt in its own order.
 */
export function sentenceTiles(
  item: SentenceItem | undefined,
  random: () => number = Math.random,
): SentenceTile[] {
  const words = sentenceWords(item?.sentence);
  const distractors = itemsToArray(item?.distractors ?? '').map((word) =>
    word.replace(EDGE_SIGNS, ''),
  );
  const all = [...words, ...distractors].filter(Boolean);
  const tiles = all.map((word, id) => ({ id, word }));
  for (let attempt = 0; attempt < 5; attempt++) {
    for (let i = tiles.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [tiles[i], tiles[j]] = [tiles[j]!, tiles[i]!];
    }
    const inOrder = tiles
      .slice(0, words.length)
      .every((tile, i) => tile.word === words[i]);
    if (words.length < 2 || !inOrder) break;
  }
  return tiles;
}
